import { catchError, finalize, interval, of, switchMap, takeWhile, Subject, Subscription, debounceTime } from 'rxjs';
import { BatchLineComponent } from './../batch-line/batch-line.component';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  OnInit,
  OnDestroy,
  Signal,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { OperationTypeService } from '../../core/services/operation-type/operation-type.service';
import { BatchService } from '../../core/services/batch/batch.service';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { ExportbatchService } from '../request-service/exportbatch/exportbatch.service';
import { DisplayFileComponent } from '../display-file/display-file.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ModalService } from '../../core/services/modal/modal.service';
import { CustomerService } from '../../core/services/customer.service';
import { SourceTypeService } from '../../core/services/source-type/source-type.service';
import { LoadingSpinnerComponent } from '../../shared/components/loading-spinner/loading-spinner.component';
import { EditTransactionsModalComponent } from '../edit-transactions-modal/edit-transactions-modal.component';
import { DropdownComponent } from '../../shared/components/dropdown/dropdown.component';
import { IconMoreActionsComponent } from '../../shared/components/icon-more-actions/icon-more-actions.component';
import { FilesMappingService } from "../../core/services/files-mapping/files-mapping.service";
import { SkeletonDirective } from '../../shared/directives/skeleton.directive';
import { FolderFilesComponent } from '../../shared/components/folder-files/folder-files.component';
import { ZipService } from '../../core/services/zip/zip.service';

import { ButtonComponent } from '../../shared/components/button/button.component';
import { InputComponent } from '../../shared/components/input/input.component';
import { SelectComponent } from '../../shared/components/select/select.component';

@Component({
  selector: 'app-create-batch',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    RouterModule,
    BatchLineComponent,
    DisplayFileComponent,
    TranslatePipe,
    LoadingSpinnerComponent,
    EditTransactionsModalComponent,
    DropdownComponent,
    IconMoreActionsComponent,
    SkeletonDirective,
    FolderFilesComponent,
    ButtonComponent,
    InputComponent,
    SelectComponent
  ],
  templateUrl: './create-batch.component.html',
  styleUrl: './create-batch.component.css'
})
export class CreateBatchComponent implements OnInit, OnDestroy {
  private exportbatchService = inject(ExportbatchService);
  private filesMappingService = inject(FilesMappingService);
  private zipService = inject(ZipService);

  private manualFieldOverrideSubject = new Subject<void>();
  private manualFieldOverrideSubscription: Subscription;
  private customerSub?: Subscription;
  private queryParamsSub?: Subscription;

  loading: boolean = false;
  batchForm!: FormGroup;
  internalSourceTypeFile: File[] | null;
  externalSelectedFiles: File[] = [];
  reconciliations = signal<any[]>([]);
  listOperationTypes!: any;
  batchId: number;
  batch = signal<any>(null);
  code: string;
  uploadProgress: number = 0;
  numberSourceType = signal<number>(0);
  numberReconciliation = signal<number>(0);
  existedSourceTypes = signal<number>(0);
  reconciliationsWithExternalFilesCount = signal<number>(0);
  control = signal<number>(0);
  controlRelauchReconciliation = signal<number>(0);
  isConfig = signal<boolean>(false);
  sourceTypesBundle = signal<any[]>([]);
  showFilesBySourceType = signal<{ [key: string]: boolean }>({});
  isBankReconciliation = signal<boolean>(false)
  canReplayReconciliations = signal<boolean>(false)
  uploadedFilesBySourceType: { [sourceTypeCode: string]: File[] } = {};
  operationType: any;
  showBatchDetails = false;
  error: string | null;
  isSaving = signal<boolean>(false);
  isExtractingInternal: boolean = false;
  isExtractingExternal: boolean = false;
  internalSourcePaths: any[];
  classicSourcePaths: string[] = [];
  selectedCustomer: any;
  customerId: number;
  showEditModal = false;
  results: any[] = [];
  manualFieldsOverrides: any[] = [];
  editableBundleFields = signal<any>(null)
  nativeInternalVirtualSourcePath = signal<string | null>(null);
  latestInternalVirtualSourcePath = signal<string | null>(null);
  isDownloadingNative = signal<boolean>(false);
  isDownloadingLatest = signal<boolean>(false);
  showInternalSourceFiles = signal<boolean>(false);
  loadingOpTypes = signal<boolean>(false);
  sourceTypesBundleLoading = signal<boolean>(false);

  get operationTypeItems(): any[] {
    if (this.listOperationTypes && this.listOperationTypes.length > 0) {
      return this.listOperationTypes;
    }
    const currentOpType = this.batch()?.operationType;
    return currentOpType ? [currentOpType] : [];
  }

  optionItems = computed(() => {
    const items = [];

    if (this.nativeInternalVirtualSourcePath()) {
      items.push({
        label: this.translate.instant('CREATE_BATCH.HEADER.ORIGINAL_BUNDLE'),
        src: '../../../assets/exportation.png',
        callback: () => this.downloadSource(this.nativeInternalVirtualSourcePath(), 'native'),
        loading: this.isDownloadingNative(),
        disabled: () => false
      });
    }

    if (this.latestInternalVirtualSourcePath()) {
      items.push({
        label: this.translate.instant('CREATE_BATCH.HEADER.MODIFIED_BUNDLE'),
        src: '../../../assets/exportation.png',
        callback: () => this.downloadSource(this.latestInternalVirtualSourcePath(), 'latest'),
        loading: this.isDownloadingLatest(),
        disabled: () => false
      });
    }

    items.push({
      label: this.translate.instant('CREATE_BATCH.HEADER.EXPORT_BATCH'),
      src: '../../../assets/exportation.png',
      callback: () => this.exportXls(),
      loading: this.isLoading(),
      disabled: () => this.isLoading() || this.isReplayDisabled()
    });

    if (this.editableBundleFields()) {
      items.push({
        label: this.translate.instant('CREATE_BATCH.HEADER.EDIT_TRANSACTIONS'),
        icon: 'square-pen',
        callback: () => this.openEditModal(),
        disabled: () => false
      });
    }

    return items;
  });

  private endpointPrefix = environment.apiUrl;

  showSourceTypesBundle = computed(() => this.sourceTypesBundle().length > 0 && !this.batchId);

  isReconciling: Signal<boolean> = computed(() => {
    if ((this.existedSourceTypes() === this.control()) && (this.reconciliationsWithExternalFilesCount() === this.controlRelauchReconciliation())) {
      return false;
    }
    return true;
  });

  isReplayDisabled: Signal<boolean> = computed(() => {
    if (this.isReconciling() || this.isSaving()) {
      return true;
    }
    if (!this.batch() || !this.batch().reconciliations) {
      return false;
    }
    return this.batch().reconciliations.some((rec: any) =>
      rec.status && !(rec.status === 'DONE' || rec.status === 'RECONCILIATION_ERROR' || rec.status === 'TRANSFORMATION_ERROR')
    );
  });

  constructor(
    private fb: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private operationTypeService: OperationTypeService,
    private batchService: BatchService,
    private http: HttpClient,
    private modalService: ModalService,
    private translate: TranslateService,
    private customerService: CustomerService,
    private sourceTypeService: SourceTypeService
  ) { }

  ngOnInit(): void {
    this.manualFieldOverrideSubscription = this.manualFieldOverrideSubject.pipe(
      debounceTime(500)
    ).subscribe(() => {
      this.getManualFieldOverrideInfos();
    });

    this.batchForm = this.fb.group({
      name: ['', Validators.required],
      operationType: ['', Validators.required],
    });

    this.batchId = Number(this.activatedRoute.snapshot.params['id']);

    this.initCustomer();

    if (this.batchId) {
      this.getBatchById();
    } else {
      this.batchForm.get('operationType')?.valueChanges.subscribe((value: any) => {
        this.handleOperationTypeChange(value);
      });
    }
  }

  private getCustomerIdFromUrl(): number | null {
    const queryParamId = this.activatedRoute.snapshot.queryParamMap.get('customerId');
    return queryParamId ? Number(queryParamId) : null;
  }

  private initCustomer(): void {
    const customerIdFromUrl = this.getCustomerIdFromUrl();

    if (customerIdFromUrl) {
      this.customerId = customerIdFromUrl;
      const currentCustomer = this.customerService.getCustomer();
      if (currentCustomer && currentCustomer.id === customerIdFromUrl) {
        this.selectedCustomer = currentCustomer;
        if (!this.batchId) {
          this.getOperationTypes();
        }
      } else {
        this.customerService.getCustomerById(customerIdFromUrl).subscribe({
          next: (customer: any) => {
            if (customer) {
              this.selectedCustomer = customer;
              this.customerId = customer.id;
              this.customerService.setCustomer(customer);
              if (!this.batchId) {
                this.getOperationTypes();
              }
            }
          },
          error: (err: any) => {
            console.error('Error fetching customer by id', err);
            if (!this.batchId) {
              this.getOperationTypes();
            }
          }
        });
      }
    } else {
      const currentUserRaw = localStorage.getItem('currentUser');
      if (currentUserRaw) {
        try {
          const currentUser = JSON.parse(currentUserRaw);
          if (currentUser?.customerId) {
            this.customerId = Number(currentUser.customerId);
          }
        } catch (e) {
          console.warn('Unable to parse currentUser from storage', e);
        }
      }
      this.selectedCustomer = this.customerService.getCustomer();
      if (this.selectedCustomer) {
        this.customerId = this.selectedCustomer.id;
      }
      if (!this.batchId) {
        this.getOperationTypes();
      }
    }

    this.customerSub = this.customerService.selectedCustomer$.subscribe((customer: any) => {
      const urlCustomerId = this.getCustomerIdFromUrl();
      if (urlCustomerId && customer && customer.id !== urlCustomerId) {
        return;
      }
      if (customer) {
        const customerChanged = !this.selectedCustomer || this.selectedCustomer.id !== customer.id;
        this.selectedCustomer = customer;
        this.customerId = customer.id;
        if (!this.batchId && customerChanged) {
          this.getOperationTypes();
        }
      }
    });

    this.queryParamsSub = this.activatedRoute.queryParams.subscribe((params: any) => {
      const customerIdParam = params['customerId'];
      if (customerIdParam) {
        const id = Number(customerIdParam);
        if (this.selectedCustomer?.id !== id) {
          this.customerId = id;
          const currentCustomer = this.customerService.getCustomer();
          if (currentCustomer && currentCustomer.id === id) {
            this.selectedCustomer = currentCustomer;
            if (!this.batchId) {
              this.getOperationTypes();
            }
          } else {
            this.customerService.getCustomerById(id).subscribe({
              next: (customer: any) => {
                if (customer) {
                  this.selectedCustomer = customer;
                  this.customerId = customer.id;
                  this.customerService.setCustomer(customer);
                  if (!this.batchId) {
                    this.getOperationTypes();
                  }
                }
              },
              error: (err: any) => console.error(err)
            });
          }
        }
      }
    });
  }

  ngOnDestroy(): void {
    if (this.manualFieldOverrideSubscription) {
      this.manualFieldOverrideSubscription.unsubscribe();
    }
    this.customerSub?.unsubscribe();
    this.queryParamsSub?.unsubscribe();
    this.manualFieldOverrideSubject.complete();
  }

  getEditableBundleFields() {
    const config = this.batch()?.operationType.configuration;
    if (!config) return null;
    return JSON.parse(config)?.editable_bundle_fields ?? null;
  }

  getBatchById() {
    this.showBatchDetails = true;
    this.batchService.getBatchById(this.batchId).subscribe(
      (res: any) => {
        this.batch.set(res);
        this.editableBundleFields.set(this.getEditableBundleFields());
        this.batchForm.patchValue({
          name: this.batch()?.name,
          operationType: this.batch()?.operationType.code,
        });
        if (this.batch()?.operationType?.bundle) this.isBankReconciliation.set(true);
        this.batchForm.get('name')?.disable();
        this.batchForm.get('operationType')?.disable();
        this.isConfig.set(this.batch()?.operationType?.configuration?.length > 0);
        this.prepareList(this.batch());
        this.numberSourceType.set(this.batch()?.sourceTypes.length);
        this.numberReconciliation.set(this.batch()?.reconciliations.length);
        this.canReplayReconciliations.set(this.batch()?.replayReconciliations)

        this.nativeInternalVirtualSourcePath.set(this.batch()?.internalVirtualSourcePaths[0]?.path);
        this.latestInternalVirtualSourcePath.set(this.batch()?.internalVirtualSourcePaths[0]?.latestPath);
        this.showBatchDetails = false;
        if (this.isBankReconciliation()) {
          this.internalSourcePaths = this.groupInternalSourcePathsByCode(this.batch()?.internalSourcePaths);
        }
        else {
          this.internalSourcePaths = this.batch()?.internalSourcePaths;
          this.classicSourcePaths = this.internalSourcePaths?.map((p: any) => p.path) || [];
        }
      },
      (err: any) => {
        this.showBatchDetails = false;
        alert('err: ' + err);
      }
    );
  }

  launchAndCheckReport() {
    this.exportbatchService.isLoading.set(true);
    this.exportbatchService.launchReport(this.batch()?.id).subscribe((launchRes: any) => {
      interval(5000)
        .pipe(
          switchMap(() => {
            return this.exportbatchService.checkReport(this.batch()?.id)
          }),
          takeWhile(res => {
            const keepGoing = !res.available && res.status !== 'REPORT_GENERATION_FAILED';
            return keepGoing;
          }, true),
          catchError(err => {
            this.modalService.notifier(this.translate.instant("CREATE_BATCH.MESSAGES.ERROR_CHECKING_REPORT"), "", "error");
            this.exportbatchService.isLoading.set(false);
            return of({ available: false });
          })
        )
        .subscribe((res: any) => {
          if (res.status === 'REPORT_GENERATION_FAILED') {
            this.modalService.notifier(this.translate.instant("CREATE_BATCH.MESSAGES.FALLED_TO_GENERATE_REPORT"), "", "error");
            this.exportbatchService.isLoading.set(false);
            return;
          }
          if (res.available) {
            const link = document.createElement('a');
            link.href = res.presigneUrl;
            link.download = 'rapport.xlsx';
            link.click();
            this.exportbatchService.isLoading.set(false);
          }
        }
        );
    });
  }

  async exportXls() {
    if (this.isConfig()) {
      const system_selector = JSON.parse(this.batch()?.operationType.configuration).system_selector;
      if (system_selector === 'SERVICE') {
        this.launchAndCheckReport();
      }
      else {
        await this.exportbatchService.exportXls(
          this.reconciliations(),
          this.batch()?.operationType
        );
      }
    } else {
      alert('config required');
    }
  }

  isLoading(): boolean {
    return this.exportbatchService.isLoading();
  }

  getOperationTypes() {
    this.loadingOpTypes.set(true);
    this.operationTypeService.getAllOperationTypes(this.selectedCustomer?.id, true).subscribe(
      (res: any) => {
        this.listOperationTypes = res;
        this.loadingOpTypes.set(false);
        const currentOpType = this.batchForm?.get('operationType')?.value;
        if (currentOpType && !this.listOperationTypes?.some((op: any) => op.code === currentOpType)) {
          this.batchForm?.get('operationType')?.reset('');
        }
      },
      (err) => {
        this.loadingOpTypes.set(false);
        alert('err: ' + err);
      }
    );
  }

  onSubmit() {
    if (!this.batchForm.valid) {
      this.batchForm.markAllAsTouched();
    }
    else {
      const formData = new FormData();
      if (this.isBankReconciliation()) {
        formData.append('name', this.batchForm.value.name);
        formData.append('operationTypeCode', this.batchForm.value.operationType);
        for (const [sourceTypeCode, files] of Object.entries(this.uploadedFilesBySourceType)) {
          files.forEach(file => {
            formData.append('sourceFiles', file);
            formData.append('sourceTypeCodes', sourceTypeCode);
          });
        }
        this.loading = true;
        this.isSaving.set(true);
        this.batchService.createReconciliationBatchWithMultipleSource(formData, this.selectedCustomer?.id).
          pipe(finalize(() => this.isSaving.set(false))
          ).
          subscribe(
            (event: HttpEvent<any>) => {
              if (event.type === HttpEventType.UploadProgress) {
                this.uploadProgress = Math.round(
                  (event.loaded / (event.total || 1)) * 100
                );
              } else if (event.type === HttpEventType.Response) {
                this.loading = false;
                this.router.navigate(['/backoffice/details-batch', event.body.id], { queryParamsHandling: 'merge' });
              }
            },
            (err: any) => {
              this.loading = false;
              this.modalService.notifier(this.translate.instant("CREATE_BATCH.MESSAGES.ERROR_CREATING_BATCH"), "", "error");
            }
          )
      }
      else {
        formData.append('name', this.batchForm.value.name);
        if (this.internalSourceTypeFile) {
          for (let file of this.internalSourceTypeFile) formData.append('sources', file);
        }
        formData.append('operationTypeCode', this.batchForm.value.operationType);
        this.loading = true;
        this.isSaving.set(true);
        this.batchService.createBatch(formData, this.selectedCustomer?.id).
          pipe(finalize(() => this.isSaving.set(false))
          ).subscribe(
            (event: HttpEvent<any>) => {
              if (event.type === HttpEventType.UploadProgress) {
                this.uploadProgress = Math.round(
                  (event.loaded / (event.total || 1)) * 100
                );
              } else if (event.type === HttpEventType.Response) {
                this.loading = false;
                this.router.navigate(['/backoffice/details-batch', event.body.id], { queryParamsHandling: 'merge' });
              }
            },
            (err: any) => {
              this.loading = false;
              this.modalService.notifier(this.translate.instant("CREATE_BATCH.MESSAGES.ERROR_CREATING_BATCH"), "", "error");
            }
          );
      }
    }
  }

  async onFileSelected(event: any, index: number) {
    if (index === 0) this.isExtractingInternal = true;
    if (index === 1) this.isExtractingExternal = true;

    try {
      const selectedFiles = Array.from(event.target.files) as File[];
      let files: File[] = [];

      for (const file of selectedFiles) {
        if (file.name.toLowerCase().endsWith('.zip')) {
          const extractedFiles = await this.zipService.extractFiles(file);
          files.push(...extractedFiles);
        }
        else {
          files.push(file);
        }
      }
      if (index === 0) {
        if (files && files.length > 0) {
          if (this.isBankReconciliation()) {
            if (this.sourceTypesBundle().length > 0) {
              this.error = null;
              this.sourceTypesBundle().forEach(source => {
                const res = this.filesMappingService.filterAndMapFiles(source, files, this.batchId);
                if (this.uploadedFilesBySourceType[source.code]?.length > 0 && res.files.length > 0) {
                  this.uploadedFilesBySourceType[source.code] = [];
                }
                const existingFiles = this.uploadedFilesBySourceType[source.code] || [];
                const newFiles = res.files || [];
                this.uploadedFilesBySourceType[source.code] = [...existingFiles, ...newFiles];
                if (res.error) {
                  source.error = res.error;
                  this.error = this.translate.instant("CREATE_BATCH.MESSAGES.FILE_INVALID");
                } else {
                  source.error = null;
                }
              });
            }
          } else {
            this.internalSourceTypeFile = files;
          }
        }
      }
      if (index === 1) {
        if (files && files.length > 0) {
          this.externalSelectedFiles = files;
          this.prepareList(this.batch(), this.externalSelectedFiles);
          this.control.set(0);
          this.controlRelauchReconciliation.set(0);
        }
      }
    } finally {
      if (index === 0) this.isExtractingInternal = false;
      if (index === 1) this.isExtractingExternal = false;
      event.target.value = '';
    }
  }

  downloadSource(sourceName: string | null, type?: 'native' | 'latest') {
    if (sourceName) {
      if (type === 'native') this.isDownloadingNative.set(true);
      if (type === 'latest') this.isDownloadingLatest.set(true);
      this.http
        .get(
          this.endpointPrefix + `/reconciliations/download?file=${sourceName}`,
          { responseType: 'blob' }
        )
        .pipe(
          finalize(() => {
            if (type === 'native') this.isDownloadingNative.set(false);
            if (type === 'latest') this.isDownloadingLatest.set(false);
          })
        )
        .subscribe((data: Blob) => {
          const blob = new Blob([data], { type: 'application/zip' });
          const url = window.URL.createObjectURL(blob);

          const link = document.createElement('a');

          link.href = url;
          link.download = sourceName;
          link.click();

          window.URL.revokeObjectURL(url);
        });
    }
  }

  prepareList(batch: any, externalSelectedFiles?: any[]) {
    const sourceTypes = batch?.sourceTypes
      .filter(
        (elt: any) =>
          !batch?.reconciliations.some(
            (rec: any) => rec?.source1?.name === elt.name
          )
      )
      .map((elt: any) => {
        let children = elt.sourceTypes.length > 0 ? elt.sourceTypes : [];
        const resElt = this.filesMappingService.filterAndMapFiles(elt, externalSelectedFiles, this.batchId);
        let cExternalSelectedFile = resElt.files;
        let lineError = resElt.error || null;

        const mappedChildren = children.map((child: any) => {
          const resChild = this.filesMappingService.filterAndMapFiles(child, externalSelectedFiles, this.batchId);
          if (resChild.error) lineError = resChild.error;
          return {
            source1: child,
            externalSelectedFile: resChild.files,
          }
        });

        if (elt.type === 'VIRTUAL') {
          const virtualFiles = new Set<File>();
          mappedChildren.forEach((child: any) => {
            if (child.externalSelectedFile && child.externalSelectedFile.length > 0) {
              child.externalSelectedFile.forEach((f: File) => virtualFiles.add(f));
            }
          });
          if (virtualFiles.size > 0) {
            cExternalSelectedFile = Array.from(virtualFiles);
          }
        }

        return {
          source1: elt,
          externalSelectedFile: cExternalSelectedFile,
          children: mappedChildren,
          error: lineError
        };
      });
    const reconciliations = batch?.reconciliations.map((elt: any) => {
      const resElt = this.filesMappingService.filterAndMapFiles(elt?.source1, externalSelectedFiles, this.batchId);
      let cExternalSelectedFile = resElt.files;
      let lineError = resElt.error || null;

      const fullSourceType = batch.sourceTypes?.find(
        (st: any) => st.id === elt.source1?.id || st.code === elt.source1?.code
      );

      let mappedChildren: any[];
      if (fullSourceType?.type === 'VIRTUAL' && fullSourceType?.sourceTypes?.length > 0) {
        const submittedSources = elt.sources ?? [];
        mappedChildren = (elt.children != null && elt.children?.length > 0) ? elt.children : fullSourceType.sourceTypes.map((bundleSourceType: any) => {
          const submitted = submittedSources.find(
            (s: any) => s.sourceType?.id === bundleSourceType.id || s.sourceType?.code === bundleSourceType.code
          );
          return {
            source1: bundleSourceType,
            externalSelectedFile: submitted?.sourcePaths ?? [],
          };
        });
      } else {
        const bundleSources = elt.sources?.length > 0
          ? elt.sources.filter((current: any) => current.sourceType.type === 'BUNDLE')
          : [];
        mappedChildren = bundleSources.map((child: any) => ({
          source1: child.sourceType,
          externalSelectedFile: child?.sourcePaths,
        }));
      }

      return {
        ...elt,
        externalSelectedFile: cExternalSelectedFile,
        children: mappedChildren,
        error: lineError
      }
    });
    this.reconciliations.set([...reconciliations, ...sourceTypes]);
    this.reconciliations().sort(
      (a: { source1: { name: string } }, b: { source1: { name: any } }) =>
        a.source1.name.localeCompare(b.source1.name)
    );
    this.existedSourceTypes.set(
      sourceTypes.filter((elt: any) => elt.externalSelectedFile?.length > 0).length
    );

    this.reconciliationsWithExternalFilesCount.set(
      reconciliations.filter((elt: any) => elt.externalSelectedFile?.length > 0).length
    );

  }

  updateReconciliations(newReconciliation: any) {
    this.numberReconciliation.update((current) => current + 1);
    if (this.control() < this.existedSourceTypes()) {
      this.control.update((current) => current + 1);
    }

    if (this.batch()?.reconciliations) {
      const index = this.batch()?.reconciliations.findIndex(
        (rec: any) => rec.id === newReconciliation.id
      );
      if (index !== -1) {
        this.batch.update((current) => {
          const updatedReconciliations = [...current.reconciliations];
          updatedReconciliations[index] = newReconciliation;
          return { ...current, reconciliations: updatedReconciliations };
        });
      } else if (this.batch().reconciliations.length < this.numberReconciliation()) {
        this.batch.update((current) => {
          const updatedReconciliations = [...current.reconciliations, newReconciliation];
          return { ...current, reconciliations: updatedReconciliations };
        });
      }
    }
  }

  updateRelauchReconciliations(reconciliation: any) {
    if (this.controlRelauchReconciliation() < this.reconciliationsWithExternalFilesCount()) {
      this.controlRelauchReconciliation.update((current) => current + 1);
    }
    if (this.batch()?.reconciliations) {
      const index = this.batch()?.reconciliations.findIndex(
        (rec: any) => rec.id === reconciliation.id
      );
      if (index !== -1) {
        this.batch.update((current) => {
          const updatedReconciliations = [...current.reconciliations];
          updatedReconciliations[index] = reconciliation;
          return { ...current, reconciliations: updatedReconciliations };
        });
      }
    }
  }

  handleReconciliationStatusChange(updatedReconciliation: any) {
    const index = this.batch().reconciliations.findIndex(
      (rec: any) => rec.id === updatedReconciliation.id
    );
    if (index !== -1) {
      const oldStatus = this.batch().reconciliations[index].status;
      this.batch.update((current) => {
        const updatedReconciliations = [...current.reconciliations];
        updatedReconciliations[index] = updatedReconciliation;
        return { ...current, reconciliations: updatedReconciliations };
      });
      if (oldStatus !== updatedReconciliation.status && (updatedReconciliation.status === 'DONE' || updatedReconciliation.status === 'RECONCILIATION_ERROR' || updatedReconciliation.status === 'TRANSFORMATION_ERROR')) {
        this.manualFieldOverrideSubject.next();
      }
    }
  }

  onShowFiles(sourceTypeCode: string) {
    this.showFilesBySourceType.update(currentVisibility => {
      const newVisibility = { ...currentVisibility };
      newVisibility[sourceTypeCode] = !newVisibility[sourceTypeCode];
      return newVisibility;
    });
  }

  groupInternalSourcePathsByCode(internalSourcePaths: any[]): any[] {
    const grouped = Object.values(
      internalSourcePaths.reduce((acc, curr) => {
        const code = curr.sourceTypeCode;

        if (!acc[code]) {
          acc[code] = {
            sourceTypeCode: curr.sourceTypeCode,
            sourceTypeName: curr.sourceTypeName,
            paths: [],
          };
        }

        acc[code].paths.push(curr.path);
        return acc;
      }, {} as Record<string, { sourceTypeCode: string, sourceTypeName: string, paths: string[] }>)
    );

    return grouped;
  }

  handleOperationTypeChange(selectedCode: string): void {
    this.sourceTypesBundle.set([]);
    this.internalSourceTypeFile = [];
    this.uploadedFilesBySourceType = {};
    this.isBankReconciliation.set(false);
    this.operationType = this.listOperationTypes?.find((operationType: any) => operationType.code === selectedCode);
    if (this.operationType?.bundle) {
      this.sourceTypesBundleLoading.set(true);
      this.sourceTypeService.getSourceTypesByBundleId(this.operationType.bundle.id, this.selectedCustomer?.id)
        .subscribe(
          (res: any) => {
            this.sourceTypesBundle.set(res);
            this.sourceTypesBundleLoading.set(false);
          },
          (err: any) => {
            this.sourceTypesBundleLoading.set(false);
            this.modalService.notifier(this.translate.instant("CREATE_BATCH.MESSAGES.ERROR_OCCURRED"), "", "error");
          }
        )
      this.isBankReconciliation.set(true);
    }
  }

  replayReconciliationBatch() {
    this.isSaving.set(true);
    this.batchService.replayReconciliationBatch(this.batchId).pipe(finalize(() => this.isSaving.set(false))
    ).subscribe(
      (res: any) => {
        this.batch.set(res);
        this.getBatchById();
      },
      (err: any) => {
        alert('err: ' + err);
      }
    );

  }

  openEditModal() {
    this.showEditModal = true;
    this.loading = true;
    this.batchService.getTransactionsForBatch(this.batchId).subscribe(
      (res: any) => {
        this.results = res.data;
        this.manualFieldsOverrides = res.manualFieldOverrides;
        this.loading = false;
      },
      (err: any) => {
        alert('err: ' + err);
        this.loading = false;
      }

    );
  }

  closeEditModal() {
    this.showEditModal = false;
    this.getManualFieldOverrideInfos();
  }

  editTransactionsModal() {
    alert('edit transactions');
  }

  replayBundle() {
    if (this.editableBundleFields()) {
      this.batchService.replayBundle(this.batchId).pipe(finalize(() => this.isSaving.set(false))
      ).subscribe(
        (res: any) => {
          this.batch.set(res);
          this.getBatchById();
        },
        (err: any) => {
          alert('err: ' + err);
        }
      );
    }
  }

  getManualFieldOverrideInfos() {
    this.batchService.getManualFieldOverrideInfos(this.batchId).subscribe(
      (res: any) => {
        this.nativeInternalVirtualSourcePath.set(res?.internalVirtualSourcePaths[0]?.path);
        this.latestInternalVirtualSourcePath.set(res?.internalVirtualSourcePaths[0]?.latestPath);
        this.canReplayReconciliations.set(res?.replayReconciliations)
      },
      (err: any) => {
        console.log('err: ' + err);
      }
    );
  }

  hasBundleFilesToUpload(): boolean {
    return Object.values(this.uploadedFilesBySourceType).some(files => files.length > 0);
  }

  onShowInternalSourceFiles() {
    this.showInternalSourceFiles.update(current => !current);
  }

  removeInternalFile(filename: string) {
    this.internalSourceTypeFile =
      (this.internalSourceTypeFile || []).filter(
        (file: File) => file.name !== filename
      );
  }

  removeSourceBankFile(sourceTypeCode: string, filename: string) {
    if (this.isBankReconciliation()) {
      const sourceType = this.sourceTypesBundle().find((st: any) => st.code === sourceTypeCode);
      if (sourceType) {
        const updatedFiles = (this.uploadedFilesBySourceType[sourceTypeCode] || []).filter(
          (file: File) => file.name !== filename
        );
        this.uploadedFilesBySourceType[sourceTypeCode] = updatedFiles;
      }
    }
  }


}
